import {
    getCategoryHref,
    getEventsHref,
    getMerchantHref,
    getProductDetailHref,
    getProductMerchantHref,
    getPromotionHref,
} from '@/constants/hooks';
import { getNestedValue } from './dataBinding';

type ThemeLinkContext = {
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
};

const isRenderableHref = (value: unknown): value is string => {
    return typeof value === 'string' && value.trim().length > 0 && value.trim() !== '#';
};

const firstString = (...values: unknown[]): string => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim();
        }
    }
    return '';
};

const resolveSlugType = (item: any, fallback = 'slug'): string => {
    return firstString(item?.slug_type, fallback) || 'slug';
};

const resolveMerchant = (item: any): any => {
    // By the time this renders, the item has usually already gone through
    // template-renderer's normalizeItem, which flattens item.merchant/.offer
    // into the item itself and never preserves a populated .merchant
    // sub-object — so an empty {} here is the common case, not a real
    // merchant, and must fall through to the flat item or every merchant
    // field lookup below silently resolves to nothing.
    if (item?.merchant && typeof item.merchant === 'object' && Object.keys(item.merchant).length > 0) {
        return item.merchant;
    }

    return item;
};

export const buildMerchantHref = (item: any, context?: ThemeLinkContext): string => {
    const merchant = resolveMerchant(item);
    const storeSlug = firstString(context?.store_slug, merchant?.store_slug);
    const slugType = resolveSlugType(item, context?.slug_type || merchant?.slug_type || 'slug');

    if (!storeSlug) {
        return '#';
    }

    const merchantSlug = firstString(
        merchant?.[slugType],
        merchant?.slug,
        merchant?.merchant_slug,
        merchant?.store_slug
    );

    if (!merchantSlug) {
        return '#';
    }

    return getMerchantHref(merchant, storeSlug, slugType);
};

export const buildCategoryHref = (item: any, context?: ThemeLinkContext): string => {
    const category = item?.category && typeof item.category === 'object' ? item.category : item;
    const categorySlug = firstString(category?.slug, category?.category_slug, category?.cat_slug);
    const categoryPath = firstString(context?.category_slug, category?.category_slug, category?.cat_slug, 'category');
    const slugType = resolveSlugType(category, context?.slug_type || 'slug');

    if (!categorySlug) {
        return '#';
    }

    return getCategoryHref(category, categoryPath, slugType);
};

export const buildPromotionHref = (item: any, context?: ThemeLinkContext): string => {
    const promotion = item?.promotion && typeof item.promotion === 'object' ? item.promotion : item;
    const promotionPath = firstString(context?.promotion_slug, promotion?.promotion_slug, 'promotion');
    const promotionSlug = firstString(promotion?.slug, promotion?.promotion_slug);

    if (!promotionSlug) {
        return '#';
    }

    return getPromotionHref(promotion, promotionPath);
};

export const buildEventHref = (item: any): string => {
    const event = item?.event && typeof item.event === 'object' ? item.event : item;
    const slugType = resolveSlugType(event, event?.slug_type || 'slug');
    const eventSlug = firstString(event?.[slugType], event?.slug, event?.event_slug);

    if (!eventSlug) {
        return '#';
    }

    return getEventsHref(event, slugType);
};

export const buildProductHref = (item: any, context?: ThemeLinkContext): string => {
    const product = item?.product && typeof item.product === 'object' ? item.product : item;
    const storeSlug = firstString(context?.store_slug, product?.store_slug);
    const slugType = resolveSlugType(product, context?.slug_type || product?.slug_type || 'slug');
    const productSlug = firstString(product?.[slugType], product?.slug, product?.product_slug);
    const categorySlug = firstString(product?.category?.slug, product?.category_slug);

    if (!productSlug) {
        return '#';
    }

    if (storeSlug) {
        return getProductDetailHref(product, slugType, productSlug, categorySlug || undefined);
    }

    return getProductMerchantHref(product, slugType);
};

// Some list APIs (e.g. categories) return their own url/category_url field already
// populated, but relative ("category/costumes" instead of "/category/costumes").
// next/link resolves a relative href against the *current* page, so on a 2-segment
// page like /store/addmotor.com that became /store/category/costumes. Force a
// leading slash on anything that isn't already absolute or external.
const toAbsolutePath = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '#' || /^([a-z][a-z0-9+.-]*:)?\/\//i.test(trimmed)) {
        return trimmed;
    }
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};
const resolveEmbeddedSelfToken = (hrefValue: string, data: unknown): string | undefined => {
    if (!hrefValue.includes('[]')) return undefined;
    const value = typeof data === 'string' ? data.trim() : '';
    if (!value) return undefined;
    return hrefValue.replaceAll('[]', encodeURIComponent(value));
};

export const resolveCustomThemeHref = (
    hrefValue: string | undefined,
    data?: any,
    context?: ThemeLinkContext
): string | undefined => {
    if (!hrefValue) {
        return undefined;
    }

    // Builder JSON can contain an unresolved hash link for dynamic event and
    // promotion rows. Resolve it from the row's slug instead of keeping `#`.
    if (hrefValue.trim() === '#' && data && typeof data === 'object') {
        if (data.event_name || data.event_slug || data.event?.slug) {
            const eventHref = buildEventHref(data);
            if (eventHref !== '#') return eventHref;
        }
        if (data.promotion_name || data.promotion_slug || data.promotion?.slug) {
            const promotionHref = buildPromotionHref(data, context);
            if (promotionHref !== '#') return promotionHref;
        }
    }

    if (!hrefValue.startsWith('[') || !hrefValue.endsWith(']')) {
        return resolveEmbeddedSelfToken(hrefValue, data) ?? hrefValue;
    }

    const placeholderKey = hrefValue.slice(1, -1);
    const directValue = getNestedValue(data, placeholderKey);

    if (isRenderableHref(directValue)) {
        return toAbsolutePath(directValue);
    }

    switch (placeholderKey) {
        case 'merchant_url':
            return buildMerchantHref(data, context);
        case 'category_url':
            return buildCategoryHref(data, context);
        case 'promotion_url':
        case 'promotion_detail_url':
            return buildPromotionHref(data, context);
        case 'event_url':
        case 'event_detail_url':
            return buildEventHref(data);
        case 'product_url':
            return buildProductHref(data, context);
        case 'read_more_url':
        case 'redirect_url':
        case 'url':
        case 'link': {
            const fallback = firstString(
                data?.url,
                data?.link,
                data?.read_more_url,
                data?.redirect_url,
                data?.merchant_url,
                data?.category_url,
                data?.promotion_url,
                data?.event_url,
                data?.product_url
            );
            return fallback ? toAbsolutePath(fallback) : undefined;
        }
        case 'out_link':
            // Outbound affiliate links are external by design - leave untouched.
            return firstString(data?.out_link, data?.url, data?.link) || undefined;
        default: {
            const fallback = firstString(
                directValue,
                data?.url,
                data?.link,
                data?.read_more_url,
                data?.redirect_url
            );
            return fallback ? toAbsolutePath(fallback) : undefined;
        }
    }
};
